import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Check } from "lucide-react";
import { FanChart, FatChart, MoistureChart } from "../site/Charts";
import { ChartCard } from "./Shared";
import { FOOTNOTE } from "../../data/products";

const ROCHE = ["#F7F0B8", "#F6EAA0", "#F5E287", "#F4D96F", "#F3CF5A", "#F2C448", "#F1B839", "#F0AB2E", "#EE9E25", "#EC901E", "#E98318", "#E57613", "#E06A10", "#DA5F0D", "#D2550B"];

const ProductTabs = ({ r }) => {
  const [idx, setIdx] = useState(0);
  const p = r.products[idx];
  return (
    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-20 md:pb-28">
      <p className="eyebrow" style={{ color: r.accent }}>The AVITIVA range</p>
      <h2 className="font-display text-4xl md:text-6xl font-light text-[var(--forest)] mt-4">Four focused solutions.</h2>
      <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-2">
        {r.products.map((x, i) => (
          <button
            key={x.name}
            data-testid={`avitiva-tab-${x.name.toLowerCase()}`}
            onClick={() => setIdx(i)}
            className={`text-left rounded-2xl border px-5 py-4 transition-[background-color,border-color,color] duration-300 ${idx === i ? "text-white border-transparent" : "border-[var(--line)] bg-[var(--paper)] hover:border-[var(--forest)]"}`}
            style={idx === i ? { background: r.accent } : undefined}
          >
            <span className="block font-display text-xl">{x.name}</span>
            <span className={`block font-mono text-[11px] mt-1 ${idx === i ? "text-white/85" : "text-[var(--muted)]"}`}>{x.dose}</span>
          </button>
        ))}
      </div>
      <div className="relative mt-6 rounded-[28px] border border-[var(--line)] bg-[var(--paper)] overflow-hidden">
        <AnimatePresence initial={false}>
          <motion.div
            key={p.name}
            data-testid="avitiva-product-panel"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0, position: "absolute", inset: 0 }}
            transition={{ duration: 0.45 }}
            className="grid md:grid-cols-2"
          >
            <div className="relative h-72 md:h-auto md:min-h-[420px] overflow-hidden">
              <motion.img initial={{ scale: 1.12 }} animate={{ scale: 1 }} transition={{ duration: 1.2 }} src={p.image} alt={p.name} className="absolute inset-0 h-full w-full object-cover" />
            </div>
            <div className="p-8 md:p-12">
              <p className="eyebrow" style={{ color: r.accent }}>{p.kind}</p>
              <p className="font-display text-5xl font-light text-[var(--forest)] mt-3">{p.name}</p>
              <p className="mt-5 text-[var(--ink)] leading-relaxed">{p.d}</p>
              <ul className="mt-6 space-y-2.5">
                {p.points.map((pt) => <li key={pt} className="flex items-center gap-3"><Check size={16} style={{ color: r.accent }} />{pt}</li>)}
              </ul>
              <p className="mt-8 font-mono text-sm" style={{ color: r.accent }}>Inclusion · {p.dose}</p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
};

const DoseTable = ({ title, rows, accent }) => (
  <div>
    <p className="font-display text-xl text-[var(--forest)] mb-3">{title}</p>
    <div className="divide-y divide-[var(--line)] border-y border-[var(--line)]">
      {rows.map(([k, v]) => (
        <div key={k} className="flex justify-between py-3 text-sm"><span>{k}</span><span className="font-mono" style={{ color: accent }}>{v}</span></div>
      ))}
    </div>
  </div>
);

export const AvitivaDetail = ({ r }) => (
  <>
    <ProductTabs r={r} />
    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-16 grid md:grid-cols-3 gap-5">
      <ChartCard tag="SENZIA · Yolk colour fan score by diet*" title="Golden-orange yolks" color="#C77A12"><FanChart h={250} /></ChartCard>
      <ChartCard tag="LYSOBOOST · Apparent fat digestibility, %*" title="Young birds digest fat better" color={r.accent}><FatChart h={250} /></ChartCard>
      <ChartCard tag="PROTIVEX · Moisture through the mill, %*" title="Moisture kept through the mill" color={r.accent2}><MoistureChart h={250} /></ChartCard>
    </section>
    <p className="mx-auto max-w-[1400px] px-5 md:px-10 text-xs text-[var(--muted)]">{FOOTNOTE}</p>

    <section className="mx-auto max-w-[1400px] px-5 md:px-10 py-20 md:py-28 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <p className="eyebrow" style={{ color: r.accent }}>SENZIA dosage guide</p>
        <h2 className="font-display text-4xl font-light text-[var(--forest)] mt-4">Dial in the colour.</h2>
        <p className="text-sm text-[var(--muted)] mt-3">Roche Yolk Colour Fan — SENZIA working range 11–15.</p>
        <div className="mt-6 flex rounded-full overflow-hidden h-10 border border-[var(--line)]" data-testid="roche-fan">
          {ROCHE.map((c, i) => (
            <div key={c} className="flex-1 grid place-items-center font-mono text-[10px] text-[var(--forest)]" style={{ background: c, outline: i >= 10 ? "2px solid rgba(14,42,31,0.35)" : "none", outlineOffset: -2 }}>{i + 1}</div>
          ))}
        </div>
      </div>
      <div className="lg:col-span-7 grid sm:grid-cols-2 gap-8">
        <DoseTable title="Egg yolk · layer feed" rows={r.senziaDose.yolk} accent={r.accent} />
        <DoseTable title="Broiler skin & shanks · finisher" rows={r.senziaDose.skin} accent={r.accent} />
      </div>
    </section>

    <section className="mx-auto max-w-[1400px] px-5 md:px-10 pb-8">
      <div className="rounded-[28px] p-7 md:p-12 overflow-x-auto" style={{ background: r.tint }}>
        <p className="eyebrow" style={{ color: r.accent }}>CURCUMAX vs turmeric powder</p>
        <table data-testid="curcumax-table" className="w-full mt-6 text-left min-w-[480px]">
          <thead>
            <tr className="font-mono text-[11px] uppercase tracking-widest text-[var(--muted)]"><th className="pb-3 font-normal"> </th><th className="pb-3 font-normal">Turmeric powder</th><th className="pb-3 font-normal" style={{ color: r.accent }}>CURCUMAX</th></tr>
          </thead>
          <tbody>
            {r.curcumaxTable.map(([k, a, b]) => (
              <tr key={k} className="border-t border-black/10"><td className="py-4 font-display text-lg text-[var(--forest)]">{k}</td><td className="py-4 text-sm text-[var(--muted)]">{a}</td><td className="py-4 text-sm font-semibold">{b}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  </>
);
